import { Fan, Gender } from "../artists/fans";
import { VirtualOptIns } from "../creator";
import { SurveyResponse, SurveyResponses } from "../functions/setfan/survey";
import { ModuleType, SurveyConfigModule } from "./fanApp";
import { PreSaveReleaseModuleData } from "./modules";

export const findPreSaveSurveyConfigModule = (
  modules: SurveyConfigModule[] = [],
): SurveyConfigModule<PreSaveReleaseModuleData> | undefined => {
  for (let index = 0; index < modules.length; index++) {
    const mod = modules[index];

    const found =
      mod.type === "PreSaveRelease"
        ? mod
        : findPreSaveSurveyConfigModule(mod.modules);

    if (found) {
      return found as SurveyConfigModule<PreSaveReleaseModuleData>;
    }
  }
};

export const findSurveyConfigModule = <D>(
  modules: SurveyConfigModule[] = [],
  type: ModuleType,
): SurveyConfigModule<D> | undefined => {
  for (let index = 0; index < modules.length; index++) {
    const mod = modules[index];

    const found =
      mod.type === type ? mod : findSurveyConfigModule(mod.modules, type);

    if (found) {
      return found as SurveyConfigModule<D>;
    }
  }
};

/**
 * Resolves the fan's opt-in status based on survey responses and configuration.
 * Calculates the `optin` map, `consent` status, and `smsOptIn` status.
 *
 * @param responses The survey responses object.
 * @param surveyVirtualOptIns The virtual opt-ins configuration from the survey.
 * @returns An object containing `optin` (map or undefined), `consent` (boolean or undefined), and `smsOptIn` (boolean or undefined).
 */
export const resolveOptin = (
  responses: SurveyResponses,
  surveyVirtualOptIns: VirtualOptIns,
): {
  optin?: Record<string, boolean>;
  consent?: boolean;
  smsOptIn?: boolean;
} => {
  const optInResponse = responses.optIn;
  const optinMap: Record<string, boolean> = {};
  let consent: boolean | undefined = undefined;
  let smsOptIn: boolean | undefined = undefined;

  optInResponse?.value?.forEach((optInValue: string) => {
    // special handling for optInTermsPrivacy1 and optInText
    if (optInValue === "optInTermsPrivacy1") {
      consent = true;
      surveyVirtualOptIns.terms?.forEach((vOptIn: string) => {
        optinMap[vOptIn] = true;
      });
    }
    if (optInValue === "optInText") {
      smsOptIn = true;
      surveyVirtualOptIns.sms?.forEach((vOptIn: string) => {
        optinMap[vOptIn] = true;
      });
    }
    // write all optin keys to optin map in fan record
    optinMap[optInValue.replace("optin-", "")] = true;
  });

  return {
    optin: Object.keys(optinMap).length > 0 ? optinMap : undefined,
    consent,
    smsOptIn,
  };
};

/**
 * Safely parses a survey response value into an integer.
 * Handles number and string types.
 *
 * @param value The survey response value.
 * @returns The parsed integer, or undefined if parsing fails or the type is invalid.
 */
const safeParseInt = (value: SurveyResponse["value"]) =>
  typeof value === "number"
    ? value
    : typeof value === "string"
    ? parseInt(value)
    : undefined;

/**
 * Safely converts a survey response value to a string.
 * If the value is a string, it's returned directly.
 * If it's an array, its elements are joined using the provided separator.
 * If it's a number, it's converted to its string representation.
 * If the value is an array, each element is processed with safeString before joining.
 *
 * @param value The survey response value.
 * @param join The separator string to use when joining array elements (default: " ").
 * @returns The string representation, or undefined if the type is invalid.
 */
export const safeString = (
  value: SurveyResponse["value"],
  join = " ",
): string | undefined =>
  typeof value === "string"
    ? value
    : typeof value === "number"
    ? String(value) // Convert number to string
    : Array.isArray(value)
    ? value
        .map((item) => safeString(item, join)) // Recursively apply safeString to each item
        .filter((item): item is string => item !== undefined) // Filter out undefined results
        .join(join) // Join the processed strings
    : undefined;

const isValidGenderKey = (key: unknown): key is Fan["gender"] =>
  typeof key === "string" && key in Gender;

/**
 * Safely extracts the fan's gender from survey responses.
 * It checks `responses.gender.value` and `responses['profiling.gender'].value`.
 * It handles array values by taking the first element.
 * It validates the result against the `Gender` enum.
 *
 * @param responses The survey responses object.
 * @returns The validated gender as a `Gender` enum member, or undefined if no valid gender could be determined.
 */
export const resolveGender = (
  responses: SurveyResponses,
): Fan["gender"] | undefined => {
  const value = (responses.gender || responses["profiling.gender"])?.value;

  const genderValue = Array.isArray(value) ? value[0] : value;
  return isValidGenderKey(genderValue) ? genderValue : undefined;
};

/**
 * Safely extracts or calculates the fan's age from survey responses.
 * It checks the following fields in order:
 * 1. `responses.age.value`
 * 2. `responses['profiling.age'].value`
 * 3. Calculates from `responses['profiling.yearborn'].value`
 *
 * @param responses The survey responses object.
 * @returns The extracted or calculated age as a number, or undefined if no valid age could be determined.
 */
export const resolveAge = (responses: SurveyResponses): number | undefined => {
  // Check responses.age
  let ageValue = responses.age?.value;
  let parsedAge = safeParseInt(ageValue);
  if (parsedAge !== undefined) {
    return parsedAge;
  }

  // Check responses["profiling.age"]
  ageValue = responses["profiling.age"]?.value;
  parsedAge = safeParseInt(ageValue);
  if (parsedAge !== undefined) {
    return parsedAge;
  }

  // Check responses["profiling.yearborn"]
  const year = safeParseInt(responses["profiling.yearborn"]?.value);
  if (year !== undefined) {
    const currentYear = new Date().getFullYear();
    // Basic validation for the year
    if (year > 1900 && year <= currentYear) {
      return currentYear - year;
    }
    // Invalid year falls through and returns undefined
  }

  // Return undefined if no valid age source is found
  return undefined;
};

/**
 * Resolves the first and last name from the survey responses.
 * It checks `responses.name.value`.
 * If the value is a string, it attempts to split it into first and last name based on whitespace (\s+).
 * For multi-part names (e.g., 'Joe Bob Law'), the last part becomes the last name ('Law')
 * and the preceding parts become the first name ('Joe Bob').
 * If the value is an array, it assumes the first element is the first name and the second is the last name.
 *
 * @param responses The survey responses object.
 * @returns An object containing `firstName` and `lastName` (both potentially undefined).
 */
export const resolveNames = (
  responses: SurveyResponses,
): { firstName?: string; lastName?: string } => {
  const nameValue = responses.name?.value;
  let firstName: string | undefined = undefined;
  let lastName: string | undefined = undefined;

  if (typeof nameValue === "string") {
    // Trim whitespace and split by one or more spaces
    const parts = nameValue.trim().split(/\s+/);
    if (parts.length > 0) {
      if (parts.length === 1) {
        firstName = parts[0] || undefined; // Handle potential empty string after trim
      } else {
        lastName = parts.pop(); // Get the last part as last name
        firstName = parts.join(" ") || undefined; // Join the remaining parts for first name
      }
    }
  } else if (Array.isArray(nameValue)) {
    // Assuming array format [firstName, lastName], ensure they are strings
    firstName = typeof nameValue[0] === "string" ? nameValue[0] : undefined;
    lastName = typeof nameValue[1] === "string" ? nameValue[1] : undefined;
  }

  return { firstName, lastName };
};
